import { toTitleCase, buildFullName, buildPhotoUrl } from '@/shared/utils/mapping';
import type { Employee, EnrolledMethods } from '../../domain/entities/Employee';
import type { EmployeeDTO, BiometricDataDTO, StructureInfoDTO } from '../dto/EmployeeDTO';
import { EXPECTED_EMBEDDING_DIM } from '@/shared/config/terminal.config';
import {
  TEMPLATE_CARD,
  TEMPLATE_CARD_ALT,
  TEMPLATE_PIN,
} from '../../domain/biometricConstants';

const AVATAR_COLORS = [
  '#2485F4', '#28C76F', '#EA5455', '#53DBD4',
  '#FF9F43', '#7367F0', '#00CFE8', '#FF6B6B',
] as const;

function colorForPerson(idPerson: number): string {
  return AVATAR_COLORS[idPerson % AVATAR_COLORS.length]!;
}

function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/**
 * Returns true when `embed` is a valid 512-d face embedding vector.
 * The ONLY source of truth for "facial enrollment" in this app.
 *
 * Note: legacy `person_embed` (128-d) and `id_template_biometric === 14` are
 * INTENTIONALLY ignored — they are residual backend fields not consumed by the
 * matcher or the deletion contract.
 */
function hasValidV2Embedding(embed: number[] | null | undefined): boolean {
  return Array.isArray(embed) && embed.length === EXPECTED_EMBEDDING_DIM;
}

function deriveEnrolledMethods(dto: EmployeeDTO): EnrolledMethods {
  const biometricData: BiometricDataDTO[] = dto.biometric_data;
  return {
    // Facial: TRUE iff person_embed_v2 is a valid 512-d array. Template-14 is irrelevant.
    face: hasValidV2Embedding(dto.person_embed_v2),
    // PIN: template 5.
    pin: biometricData.some((b) => b.id_template_biometric === TEMPLATE_PIN),
    // Card: ONLY templates 3 or 4. Other templates (1, 2, 6, 7…) are not cards.
    card: biometricData.some(
      (b) =>
        b.id_template_biometric === TEMPLATE_CARD ||
        b.id_template_biometric === TEMPLATE_CARD_ALT,
    ),
  };
}

/**
 * Finds the first structuresInfo entry matching `structureType` (case-insensitive).
 * Returns its `structure` field, or null if no match.
 */
function findStructureByType(
  infos: readonly StructureInfoDTO[],
  structureType: string,
): string | null {
  const target = structureType.toUpperCase();
  for (const info of infos) {
    if ((info.structure_type ?? '').toUpperCase() === target) {
      return info.structure;
    }
  }
  return null;
}

function deriveFaceEmbeddingV2(dto: EmployeeDTO): number[] | null {
  const v2 = dto.person_embed_v2;
  if (!Array.isArray(v2) || v2.length === 0) return null;
  if (v2.length !== EXPECTED_EMBEDDING_DIM) {
    console.warn(
      `[employeeMapper] person_embed_v2 for id_person=${dto.id_person} has dim ${v2.length} ` +
      `(expected ${EXPECTED_EMBEDDING_DIM}) — ignoring`,
    );
    return null;
  }
  return v2;
}

export function toEmployee(dto: EmployeeDTO): Employee {
  const firstName = toTitleCase(dto.first_name);
  const lastName  = toTitleCase(dto.last_name1);
  const fullName  = buildFullName(dto.first_name, dto.last_name1, dto.middle_name);
  const primaryStructure = dto.structuresInfo[0];

  return {
    idPerson:        dto.id_person,
    idEntity:        dto.id_entity,
    code:            dto.code,
    dui:             dto.dui_person,
    firstName,
    lastName,
    fullName,
    initials:        initials(firstName, lastName),
    avatarColor:     colorForPerson(dto.id_person),
    email:           dto.email.toLowerCase(),
    phone:           dto.cell_phone,
    photo:           dto.photo_rekognition ?? dto.photo ?? null,
    gender:          dto.gender === 1 ? 'male' : 'female',
    status:          dto.status === 1 ? 'active' : 'inactive',
    isAdmin:         dto.admin_device === 1,
    structures:      dto.structures,
    structureName:   primaryStructure?.structure ?? '',
    cargo:           findStructureByType(dto.structuresInfo, 'CARGO'),
    dept:            findStructureByType(dto.structuresInfo, 'DEPARTAMENTO'),
    enrolledMethods:      deriveEnrolledMethods(dto),
    faceEmbedding:        deriveFaceEmbeddingV2(dto),
    legacyFaceEmbedding:  dto.person_embed ?? null,
    needsV2Enrollment:    deriveFaceEmbeddingV2(dto) === null && (dto.person_embed?.length ?? 0) > 0,
    photoUrl:             buildPhotoUrl(dto.photo_rekognition ?? dto.photo),
  };
}
